<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { baseLoading } from "@/common/components";

const props = withDefaults(
  defineProps<{
    open: boolean;
    outsideClose?: boolean;
    loading?: boolean;
    width?: string;
  }>(),
  {
    open: false,
    outsideClose: true,
    loading: false,
    width: "50rem",
  }
);

const emit = defineEmits(["close"]);

const internalOpen = ref(props.open);

function addHidePageScroll() {
  document.querySelector("html").classList.add("no-scroll");
}

function removeHidePageScroll() {
  document.querySelector("html").classList.remove("no-scroll");
}

function sidebarClose() {
  internalOpen.value = false;

  setTimeout(() => {
    emit("close");
  }, 350);
}

function sidebarOutsideClose() {
  if (props.outsideClose) sidebarClose();
}

onBeforeUnmount(() => {
  removeHidePageScroll();
});

onMounted(() => {
  addHidePageScroll();
});
</script>

<template>
  <Teleport to="body">
    <article class="base-sidebar">
      <div class="outside" @click="sidebarOutsideClose()" />
      <section
        class="sidebar"
        :class="{
          'slide-left': props.open && internalOpen,
          'slide-right': !internalOpen,
        }"
      >
        <div class="close">
          <button class="btn btn-default" @click="sidebarClose()">x</button>
        </div>
        <div class="content">
          <template v-if="props.loading">
            <base-loading />
          </template>
          <template v-else>
            <slot />
          </template>
        </div>
      </section>
    </article>
  </Teleport>
</template>

<style lang="scss" scoped>
$width: v-bind("props.width");

.base-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 98;
  display: grid;
  grid-template-columns: 1fr auto;
  width: 100vw;
  height: 100dvh;
  background: var(--black-transparent);

  .outside {
    flex: 1;
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: $width;
    height: 100dvh;
    background: #ffffff;
    padding: 1rem;

    .close {
      button {
        padding: 0;
        width: 1.5rem;
        height: 1.5rem;
      }
    }

    .content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: auto;
    }
  }
}

@-webkit-keyframes slide-left {
  0% {
    -webkit-transform: translate($width);
    transform: translateX($width);
  }
  100% {
    -webkit-transform: translate(0);
    transform: translateX(0);
  }
}
@keyframes slide-left {
  0% {
    transform: translate($width);
  }
  100% {
    transform: translateX(0);
  }
}

@-webkit-keyframes slide-right {
  0% {
    -webkit-transform: translate(0);
    transform: translateX(0);
  }
  100% {
    -webkit-transform: translate($width);
    transform: translateX($width);
  }
}
@keyframes slide-right {
  0% {
    transform: translate(0);
  }
  100% {
    transform: translateX($width);
  }
}

.slide-left {
  -webkit-animation: slide-left 300ms both;
  animation: slide-left 300ms both;
}

.slide-right {
  -webkit-animation: slide-right 300ms both;
  animation: slide-right 300ms both;
}
</style>
